import React, { useState } from "react";
import {
  Badge, Box, Divider, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay, Flex, IconButton,
  Popover, PopoverArrow, PopoverBody, PopoverCloseButton, PopoverContent, PopoverHeader, PopoverTrigger, Portal, Spinner, Text,
  VStack, useDisclosure,
} from "@chakra-ui/react";
import { Calculator } from "lucide-react";
import { getMetricasAnuncioCalculo } from "../../services/api";
import { num, useCelular } from "../SaudeAnunciosML/comum";
import { BadgeTipo, brl } from "./base";

const pct = (v) => (v === null || v === undefined ? "—" : `${num(Number(v) * 100, 2).replace(/,00$/, "")}%`);

const ORIGEM_MARGEM = { marca: "da marca", fornecedor: "do fornecedor", global: "geral" };

const TITULO = {
  padrao: "Fórmula padrão (tarifa e frete fixos)",
  real: "Com tarifa e frete reais do ML",
};

const MOTIVO_PREMIUM = {
  limite_79: "No Premium passaria de R$ 79: fica no preço Clássico",
  sem_convergencia: "Tarifa oscila na faixa de preço: fica no preço Clássico",
  tarifa_indisponivel: "Tarifa do ML indisponível: fica no preço Clássico",
  frete_indisponivel: "Frete do ML indisponível: fica no preço Clássico",
  sem_categoria: "Categoria do anúncio desconhecida",
};

function Linha({ rotulo, valor, sinal, forte, cor }) {
  return (
    <Flex justify="space-between" gap={3} fontSize="xs" fontWeight={forte ? "bold" : "normal"} color={cor || "gray.700"}>
      <Text minW={0}>{sinal && <Text as="span" color="gray.500" mr={1}>{sinal}</Text>}{rotulo}</Text>
      <Text whiteSpace="nowrap" sx={{ fontVariantNumeric: "tabular-nums" }}>{valor}</Text>
    </Flex>
  );
}

function origemFrete(c) {
  if (c.frete_origem === "cotacao_ml") return `Frete do ML (cotação${c.anuncio_referencia ? ` ${c.anuncio_referencia}` : ""})`;
  if (c.frete_origem === "fixo") return "Frete do ML (fixo acima de R$ 79)";
  return "Frete do ML (abaixo de R$ 79: não entra)";
}

function Conta({ conta, emUso }) {
  if (conta.erro) {
    return (
      <Box>
        <Text fontSize="xs" fontWeight="bold" color="gray.800" mb={1}>{TITULO[conta.chave] || conta.chave}</Text>
        <Text fontSize="xs" color="gray.600">{conta.erro}</Text>
      </Box>
    );
  }

  const c = conta.classico;
  const p = conta.premium;
  return (
    <Box>
      <Flex align="center" gap={2} mb={1} flexWrap="wrap">
        <Text fontSize="xs" fontWeight="bold" color="gray.800">{TITULO[conta.chave] || conta.chave}</Text>
        {emUso && <Badge colorScheme="green" fontSize="0.65em">em uso</Badge>}
      </Flex>
      <VStack align="stretch" spacing={0.5}>
        <Linha rotulo="Custo do produto" valor={brl(c.custo)} />
        <Linha sinal="+" rotulo={`Frete do fornecedor (${pct(c.pct_frete_fornecedor)})`} valor={brl(c.frete_fornecedor)} />
        <Linha sinal="+" rotulo="Processamento e consumíveis" valor={brl(c.processamento)} />
        <Linha sinal="+" rotulo={origemFrete(c)} valor={brl(c.frete_ml)} />
        {Number(c.taxa_fixa) > 0 && <Linha sinal="+" rotulo="Taxa fixa do ML" valor={brl(c.taxa_fixa)} />}
        <Linha sinal="=" rotulo="Custos" valor={brl(c.numerador)} forte />
        <Linha
          sinal="÷"
          rotulo={`1 − tarifa ${pct(c.tarifa)} − imposto ${pct(c.imposto)} − margem ${pct(c.mc)} ${ORIGEM_MARGEM[c.mc_origem] || ""}`}
          valor={num(c.denominador, 4)}
        />
        <Linha sinal="=" rotulo="Preço Clássico" valor={brl(c.preco)} forte cor="blue.700" />
        <Linha rotulo="Lucro por venda" valor={brl(c.lucro)} cor="gray.600" />
        {p && (
          p.convertido ? (
            <>
              <Divider my={1} />
              <Linha rotulo={`Tarifa Premium ${pct(p.pct_premium)} (Clássico ${pct(p.pct_classico)})`} valor="" />
              {p.frete_premium !== null && p.frete_premium !== undefined && Number(p.frete_premium) !== Number(p.frete_classico) && (
                <Linha rotulo="Frete no preço Premium" valor={`${brl(p.frete_classico)} → ${brl(p.frete_premium)}`} />
              )}
              <Linha sinal="=" rotulo="Preço Premium (mesmo lucro)" valor={brl(p.preco)} forte cor="purple.700" />
            </>
          ) : (
            <Text fontSize="xs" color="gray.600" mt={1}>{MOTIVO_PREMIUM[p.motivo] || p.motivo}</Text>
          )
        )}
      </VStack>
    </Box>
  );
}

function Conteudo({ dados }) {
  if (!dados) return <Flex justify="center" py={4}><Spinner size="sm" color="orange.400" /></Flex>;
  if (dados.falhou) return <Text fontSize="xs" color="red.600">Não foi possível montar o cálculo agora.</Text>;

  const emUso = (dados.contas || []).find((c) => c.chave === dados.formula_ativa && !c.erro);
  const alvo = emUso ? Number(emUso.preco_final) : null;
  const precoMl = dados.preco_ml !== null && dados.preco_ml !== undefined ? Number(dados.preco_ml) : null;
  const diverge = alvo !== null && precoMl !== null && Math.abs(alvo - precoMl) > 1;

  return (
    <VStack align="stretch" spacing={3}>
      <Flex justify="space-between" align="center" gap={2} flexWrap="wrap" fontSize="xs">
        <Flex align="center" gap={2}>
          <Text color="gray.600">No ML agora</Text>
          <BadgeTipo tipo={dados.tipo} />
        </Flex>
        <Text fontWeight="bold" color={diverge ? "orange.600" : "gray.800"}>{brl(precoMl)}</Text>
      </Flex>
      {dados.promocao && <Text fontSize="xs" color="orange.700">Produto em promoção: o preço no ML segue a promoção.</Text>}
      {diverge && !dados.promocao && <Text fontSize="xs" color="orange.700">Diferente do calculado em uso ({brl(alvo)}).</Text>}
      {dados.erro && <Text fontSize="xs" color="red.600">{dados.erro}</Text>}
      {(dados.contas || []).map((conta) => (
        <React.Fragment key={conta.chave}>
          <Divider />
          <Conta conta={conta} emUso={conta.chave === dados.formula_ativa} />
        </React.Fragment>
      ))}
    </VStack>
  );
}

export default function CalculoPreco({ idMl }) {
  const [dados, setDados] = useState(null);
  const celular = useCelular();
  const gaveta = useDisclosure();

  const carregar = async () => {
    if (dados && !dados.falhou) return;
    setDados(null);
    const r = await getMetricasAnuncioCalculo(idMl);
    setDados(r || { falhou: true });
  };

  const parar = (e) => e.stopPropagation();
  const titulo = `Cálculo do preço · ${idMl}`;
  const botao = (props) => (
    <IconButton size="sm" variant="ghost" icon={<Calculator size={16} />} aria-label={`Ver cálculo do preço de ${idMl}`} onKeyDown={parar} {...props} />
  );

  if (celular) {
    return (
      <>
        {botao({ onClick: (e) => { parar(e); gaveta.onOpen(); carregar(); } })}
        <Drawer isOpen={gaveta.isOpen} onClose={gaveta.onClose} placement="bottom">
          <DrawerOverlay />
          <DrawerContent borderTopRadius="lg" maxH="85vh" onClick={parar}>
            <DrawerCloseButton />
            <DrawerHeader fontSize="sm" pr={10}>{titulo}</DrawerHeader>
            <DrawerBody pb={6} overflowY="auto">
              <Conteudo dados={dados} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Popover isLazy placement="left-start" onOpen={carregar}>
      <PopoverTrigger>
        {botao({ onClick: parar })}
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="400px" onClick={parar} onKeyDown={parar}>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontSize="sm" fontWeight="bold" pr={8}>{titulo}</PopoverHeader>
          <PopoverBody maxH="70vh" overflowY="auto">
            <Conteudo dados={dados} />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
